import { first, sumBy } from "lodash"
import React, { createContext, useCallback, useContext, useMemo } from "react"
import { useLocation } from "@tanstack/react-router"
import { type ICollectionSimple, useGetCollections } from "~/hooks/useCollections"
import { useGetDemandVariantMetrics, useGetTrimVariantMetrics } from "~/hooks/useDSAPi"
import { useVariants } from "~/hooks/useVariants"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { useCollectionContext } from "./CollectionProvider"
import {
  useGetDemandPlanSalesForecast,
  useGetDemandPlanSeasonality,
  useGetTrimmedVariants,
} from "~/hooks/useDemandPlan"
import { VariantBaselineMetrics } from "~/server/api/routers/pythonApi"
import { api } from "~/utils/api"
import { calculateAlertLogic } from "~/utils/demandAlertUtils"
import { isMetricEffective } from "~/utils/salesMetrics"
import { useCompany } from "~/hooks/useCompany"

// Below this share of pre-orders known, expected-known is too small for a meaningful pace
// trend ((actual - expected)/expected explodes), so we report a neutral 0 instead.
export const MIN_KNOWN_PCT_FOR_PREORDER_TREND = 0.05

export enum BaselineMethodKey {
  CarryOver = "baselineForecastCarryOver", // No matches available
  NeighborsInBaseline = "baselineForecastNeighborsComparisonCollection",
  CarryOverFromOtherCollections = "baselineForecastCarryOverOtherCollection", // No matches available
  NeighborsInOtherCollections = "baselineForecastNeighborsOtherCollection",
  AvgInBaselineCollection = "baselineCollectionAvg",
  CustomMatches = "baselineForecastCustomMatches",
  None = "none",
}

const rankedBaselineStrategies: BaselineMethodKey[] = [
  BaselineMethodKey.CustomMatches,
  BaselineMethodKey.CarryOver,
  BaselineMethodKey.CarryOverFromOtherCollections,
  BaselineMethodKey.NeighborsInBaseline,
  BaselineMethodKey.NeighborsInOtherCollections,
  BaselineMethodKey.AvgInBaselineCollection,
]

type MapValue<M> = M extends Map<any, infer V> ? V : never
type TrimMetrics = MapValue<NonNullable<ReturnType<typeof useGetTrimVariantMetrics>["data"]>>
export type DemandMetricsChanges = {
  enabledSalesMetrics: string[]
  manualFinalForecast: number | null | undefined
}

// Read model — includes manualBaseline which is read-only via this API (set via NPI dialog only)
export type DemandPlanSalesForecastData = DemandMetricsChanges & { manualBaseline: number | null }
type DemandMetrics = MapValue<NonNullable<ReturnType<typeof useGetDemandVariantMetrics>["data"]>>

type VariantDemandValues = {
  forecastTotal: number
  baselineDiffUnits: number
  baselineDiffPct: number
  adjustedBaseline: number
  adjustedBaselineWithoutPreorders: number
  totalTrendAdjustment: number
  preOrderTrendDecimal: number | undefined
  preOrderFloorAdjustment: number
}

type BaselineMetrics = {
  baselineForecast: number
  preOrderQuantityBaseline: number
  expectedPreOrders: number
  adjustments: Record<string, number>
  type: "Carry-over" | "New"
  baselineMethod: string
  avgSimilarity?: number
  baselineMatchCount?: number
}
type DemandPlanSeasonality = ReturnType<typeof useGetDemandPlanSeasonality>["data"]

export type VariantWithApiMetrics = IVariantSummary &
  TrimMetrics &
  DemandMetrics &
  DemandPlanSalesForecastData

export type VariantWithBaselineMetrics = VariantWithApiMetrics &
  BaselineMetrics & {
    currentBaselineMethod: string
    // collectionIdsUsed of the selected baseline method only (drives the Baseline-info flag)
    baselineCollectionIdsUsed: number[]
  }

export type VariantWithDemandMetrics = VariantWithBaselineMetrics & VariantDemandValues

export type VariantAlertValues = {
  reviewBaseline: boolean
  forecastBelowPreOrders: boolean
}

export type VariantWithMetricsAndAlerts = VariantWithDemandMetrics & VariantAlertValues

type DemandPlanContextTypeInput = {
  sourceCollectionIds: number[]
  baselineCollectionIds: number[] | null
  demandPlanId: string | undefined
  defaultSalesMetrics: string[]
  demandPlanName: string | undefined
}

type DemandPlanContextType = {
  sourceCollectionIds: number[]
  sourceCollectionName: string | undefined
  sourceCollections: ICollectionSimple[]
  baselineCollectionIds: number[] | null
  baselineCollectionName: string | undefined
  baselineCollections: ICollectionSimple[]
  demandPlanId: string | undefined
  demandPlanName: string | undefined
  defaultSalesMetrics: string[]
  allVariants: Array<VariantWithMetricsAndAlerts>
  filteredVariants: Array<VariantWithMetricsAndAlerts>
  trimmedVariantIds: string[] | undefined
  variantsLoading: boolean
  demandPlanSeasonality: DemandPlanSeasonality
  sourceHasOuts: boolean
}

const DemandPlanContext = createContext<DemandPlanContextType | undefined>(undefined)

export const DemandPlanProvider = ({
  children,
  demandPlanContext,
}: {
  children: React.ReactNode
  demandPlanContext: DemandPlanContextTypeInput
}) => {
  const { data: companyDefaults } = useCompany()
  /*Providers / Contexts*/
  const rawVariants = useVariants()
  const { collections } = useCollectionContext()
  /*API data*/
  //Collections
  const { data: sourceCollections } = useGetCollections({
    collectionIds: demandPlanContext.sourceCollectionIds,
  })
  const { data: rawBaselineCollections } = useGetCollections({
    collectionIds: demandPlanContext.baselineCollectionIds ?? ([] as number[]),
  })
  const baselineCollections = rawBaselineCollections?.length === 0 ? null : rawBaselineCollections
  //Demand / Sales Metrics
  const { data: variantDemandMetrics, isLoading: variantDemandMetricsLoading } =
    useGetDemandVariantMetrics(
      demandPlanContext.sourceCollectionIds,
      demandPlanContext.baselineCollectionIds,
    )
  const { data: demandPlanInput, isLoading: demandInputLoading } = useGetDemandPlanSalesForecast(
    demandPlanContext.demandPlanId ?? "",
    demandPlanContext.demandPlanId != null,
  )

  // Trim
  const isTrimTab = useLocation({ select: (l) => l.pathname.endsWith("/trim") })
  const { data: variantTrimMetrics, isLoading: trimMetricsLoading } = useGetTrimVariantMetrics(
    demandPlanContext.sourceCollectionIds,
    demandPlanContext.baselineCollectionIds,
    isTrimTab,
  )
  const { data: trimmedVariantIds, isLoading: trimmedVariantIdsLoading } = useGetTrimmedVariants(
    demandPlanContext.demandPlanId ?? "",
  )

  // Seasonality data
  const { data: demandPlanSeasonality } = useGetDemandPlanSeasonality(
    demandPlanContext.demandPlanId ?? "",
    Boolean(demandPlanContext.demandPlanId),
  )

  /*State*/
  // Pre-compute variant ID set for O(1) lookup instead of O(n) .some() per variant
  const collectionVariantIdSet = useMemo(
    () => new Set(collections.flatMap((col) => col.variantIds)),
    [collections],
  )

  // Get variants that have at least 1 SKU not marked as "out"
  const collectionVariantIds = useMemo(
    () => Array.from(collectionVariantIdSet),
    [collectionVariantIdSet],
  )
  const { data: sourceVariantIds } = api.collection.getVariantsNotFullyOut.useQuery(
    { variantIds: collectionVariantIds, collectionIds: demandPlanContext.sourceCollectionIds },
    {
      enabled: collectionVariantIds.length > 0 && demandPlanContext.sourceCollectionIds.length > 0,
    },
  )
  const sourceVariantIdSet = useMemo(() => new Set(sourceVariantIds ?? []), [sourceVariantIds])

  const { data: customMatchAvgSimilarities } = api.demandNpi.getCustomMatchAvgSimilarities.useQuery(
    {
      variantIds: collectionVariantIds,
      sourceCollectionIds: demandPlanContext.sourceCollectionIds,
    },
    { enabled: collectionVariantIds.length > 0 },
  )

  // Detect whether any variants in the source collections are "fully out"
  // (i.e., all their SKUs are discontinued).
  const sourceHasOuts = useMemo(() => {
    if (sourceVariantIds === undefined) return false
    const sourceCollectionVariantIds = new Set(
      (sourceCollections ?? []).flatMap((col) => col.variantIds),
    )
    return sourceCollectionVariantIds.size > sourceVariantIds.length
  }, [sourceVariantIds, sourceCollections])

  const variants = useMemo<Array<VariantWithMetricsAndAlerts>>(() => {
    const filteredVariants = rawVariants.filter(
      (v) => collectionVariantIdSet.has(v.variantId) && sourceVariantIdSet.has(v.variantId),
    )

    const result = filteredVariants
      .map<VariantWithMetricsAndAlerts | undefined>((v) => {
        const specificVariantSalesMetrics = variantDemandMetrics?.get(v.variantId)
        const specificVariantTrimMetrics = variantTrimMetrics?.get(v.variantId)
        const demandMetrics = demandPlanInput?.get(v.variantId)
        const enabledSalesMetrics =
          demandMetrics?.enabledSalesMetrics ?? demandPlanContext.defaultSalesMetrics
        if (specificVariantSalesMetrics == undefined) {
          return undefined
        }
        const variantWithApiMetrics: VariantWithApiMetrics = {
          ...v,
          ...specificVariantSalesMetrics,
          ...(specificVariantTrimMetrics ?? ({} as TrimMetrics)),
          preOrderQuantityBaseline: specificVariantTrimMetrics?.preOrderQuantityBaseline ?? null,
          enabledSalesMetrics,
          manualFinalForecast: demandMetrics?.manualFinalForecast,
          manualBaseline: demandMetrics?.manualBaseline ?? null,
        }
        const baselineMetrics = inferBaselineMetrics(specificVariantSalesMetrics)
        if (baselineMetrics === undefined) {
          return undefined
        }
        const currentBaselineMethod = demandMetrics?.manualBaseline
          ? BaselineMethodKey.CustomMatches
          : baselineMetrics.baselineMethod
        const avgSimilarity =
          currentBaselineMethod === BaselineMethodKey.CustomMatches
            ? customMatchAvgSimilarities?.get(v.variantId)
            : baselineMetrics.avgSimilarity
        const baselineMatchCount =
          currentBaselineMethod === BaselineMethodKey.CustomMatches
            ? specificVariantSalesMetrics.baselineForecastCustomMatches?.nrMatches
            : baselineMetrics.baselineMatchCount
        const selectedExpectedPreOrders =
          currentBaselineMethod === BaselineMethodKey.CustomMatches
            ? (specificVariantSalesMetrics.baselineForecastCustomMatches?.expectedPreOrders ?? 0)
            : (baselineMetrics.expectedPreOrders ?? 0)
        // Baseline-info flag scopes to the collections of the *selected* method only, not
        // the union across all candidate methods (BaselineMethodKey values are the metrics
        // field names, so we can index by the method directly).
        const selectedMethodMetrics = (
          specificVariantSalesMetrics as Record<
            string,
            { collectionIdsUsed?: number[] } | undefined
          >
        )[currentBaselineMethod]
        const baselineCollectionIdsUsed = selectedMethodMetrics?.collectionIdsUsed ?? []
        const variantWithBaselineMetrics: VariantWithBaselineMetrics = {
          ...variantWithApiMetrics,
          ...baselineMetrics,
          avgSimilarity,
          baselineMatchCount,
          expectedPreOrders: selectedExpectedPreOrders,
          adjustments: {
            ...baselineMetrics.adjustments,
            addedQuantityExpectedPreOrder: selectedExpectedPreOrders,
          },
          baselineForecast: demandMetrics?.manualBaseline ?? baselineMetrics.baselineForecast,
          currentBaselineMethod,
          baselineCollectionIdsUsed,
        }
        const variantWithDemandMetrics: VariantWithDemandMetrics =
          applyIntermediateDemandCalculations(variantWithBaselineMetrics)
        const variantWithAlerts: VariantWithMetricsAndAlerts = {
          ...variantWithDemandMetrics,
          ...calculateAlertLogic(
            variantWithDemandMetrics,
            companyDefaults?.brandMatchQuality,
            companyDefaults?.minMatchCountThreshold,
          ),
        }
        return variantWithAlerts
      })
      .filter((v) => v !== undefined)

    return result
  }, [
    collectionVariantIdSet,
    sourceVariantIdSet,
    rawVariants,
    variantDemandMetrics,
    variantTrimMetrics,
    demandPlanInput,
    demandPlanContext,
    companyDefaults,
    customMatchAvgSimilarities,
  ])

  /*Property remapping*/
  const sourceFirstCollection = first(sourceCollections)
  const sourceCollectionName =
    sourceCollections && sourceCollections.length > 1
      ? (sourceFirstCollection?.groupName ?? undefined)
      : sourceFirstCollection?.name
  const baselineFirstCollection = first(baselineCollections)
  const baselineCollectionName =
    baselineCollections && baselineCollections.length > 1
      ? (baselineFirstCollection?.groupName ?? undefined)
      : baselineFirstCollection?.name

  const trimmedVariantIdSet = useMemo(() => new Set(trimmedVariantIds ?? []), [trimmedVariantIds])

  const filteredVariants = useMemo(
    () => variants.filter((v) => !trimmedVariantIdSet.has(v.variantId)),
    [variants, trimmedVariantIdSet],
  )

  const variantsLoading =
    variantDemandMetricsLoading ||
    demandInputLoading ||
    trimMetricsLoading ||
    trimmedVariantIdsLoading

  const outputValue = useMemo<DemandPlanContextType>(
    () => ({
      sourceCollectionIds: demandPlanContext.sourceCollectionIds,
      sourceCollections: sourceCollections ?? [],
      sourceCollectionName,
      baselineCollectionIds: demandPlanContext.baselineCollectionIds,
      baselineCollections: baselineCollections ?? [],
      baselineCollectionName,
      demandPlanId: demandPlanContext.demandPlanId,
      demandPlanName: demandPlanContext.demandPlanName,
      defaultSalesMetrics: demandPlanContext.defaultSalesMetrics,
      allVariants: variants,
      trimmedVariantIds,
      filteredVariants,
      variantsLoading,
      demandPlanSeasonality,
      sourceHasOuts,
    }),
    [
      demandPlanContext,
      sourceCollections,
      sourceCollectionName,
      baselineCollections,
      baselineCollectionName,
      variants,
      trimmedVariantIds,
      filteredVariants,
      variantsLoading,
      demandPlanSeasonality,
      sourceHasOuts,
    ],
  )

  return <DemandPlanContext.Provider value={outputValue}>{children}</DemandPlanContext.Provider>
}

export const useDemandPlanContext = () => {
  const context = useContext(DemandPlanContext)
  if (!context) {
    throw new Error("useCollectionContext must be used within a CollectionProvider")
  }
  return context
}

/**
 * Resolves the effective demand for a variant — the single source of truth for the Demand column,
 * the demand tile, and the published forecast. A manual override wins over the forecast total.
 */
export const useResolveFinalForecast = () => {
  return useCallback(
    (v: { manualFinalForecast?: number | null; forecastTotal?: number | null }) =>
      v.manualFinalForecast ?? v.forecastTotal ?? 0,
    [],
  )
}

export const applyIntermediateDemandCalculations = (
  variant: VariantWithBaselineMetrics,
): VariantWithDemandMetrics => {
  const adjustedBaseline = calculateAdjustedBaseline(variant)
  const adjustedBaselineWithoutPreorders = adjustedBaseline - variant.preOrderQuantitySource
  const riskBuy = Math.max(0, adjustedBaselineWithoutPreorders)
  const avgProductTrendDecimal = calculateAverageProductTrendDecimal(variant)

  const preOrderTrendDecimal =
    variant.preOrderQuantityBaseline > 0
      ? (variant.preOrderQuantitySource - variant.preOrderQuantityBaseline) /
        variant.preOrderQuantityBaseline
      : 0

  const expectedKnownNow = variant.preOrderQuantityBaseline - variant.expectedPreOrders
  const knownPctNow =
    variant.preOrderQuantityBaseline > 0
      ? 1 - variant.expectedPreOrders / variant.preOrderQuantityBaseline
      : 0
  const preOrderPaceTrendDecimal =
    knownPctNow >= MIN_KNOWN_PCT_FOR_PREORDER_TREND && expectedKnownNow > 0
      ? (variant.preOrderQuantitySource - expectedKnownNow) / expectedKnownNow
      : 0

  const usePacePreOrderTrend = isMetricEffective(
    variant.enabledSalesMetrics,
    "addedQuantityExpectedPreOrder",
  )
  const effectivePreOrderTrendDecimal = usePacePreOrderTrend
    ? preOrderPaceTrendDecimal
    : preOrderTrendDecimal
  const preOrderTrendFactor = variant.enabledSalesMetrics.includes("preOrderTrend")
    ? 1 + effectivePreOrderTrendDecimal
    : 1
  const totalTrendAdjustment = riskBuy * (preOrderTrendFactor * (1 + avgProductTrendDecimal) - 1)
  const variantWithIntermediateCalculations = {
    ...variant,
    adjustedBaseline,
    adjustedBaselineWithoutPreorders,
    totalTrendAdjustment,
    preOrderTrendDecimal,
  }
  const rawForecastTotal = calculateVariantTotal(variantWithIntermediateCalculations)
  const preOrderFloorAdjustment = Math.max(0, variant.preOrderQuantitySource - rawForecastTotal)
  const forecastTotal = rawForecastTotal + preOrderFloorAdjustment
  const baselineDiffUnits = forecastTotal - variant.baselineForecast
  const baselineDiffPct = 1 - forecastTotal / variant.baselineForecast
  const finalVariant = {
    ...variantWithIntermediateCalculations,
    forecastTotal,
    preOrderFloorAdjustment,
    baselineDiffUnits,
    baselineDiffPct,
  }
  return finalVariant
}

export const getOldAndNewValueForDemandAdjustment = (
  unsavedChanges: Map<string, DemandMetricsChanges>,
  variantsWithoutChanges?: VariantWithDemandMetrics[],
  variants?: VariantWithDemandMetrics[],
) => {
  const variantIds = Array.from(unsavedChanges.keys())

  const oldValue = sumBy(
    variantsWithoutChanges?.filter((v) => unsavedChanges.has(v.variantId)) ?? [],
    (v) => v.manualFinalForecast ?? v.forecastTotal,
  )

  const newValue = sumBy(variants?.filter((v) => unsavedChanges.has(v.variantId)) ?? [], (v) => {
    return v.manualFinalForecast ?? v.forecastTotal
  })

  return { oldValue, newValue, variantIds }
}

const calculateAdjustedBaseline = (
  variant: IVariantSummary & DemandMetrics & TrimMetrics & BaselineMetrics & DemandMetricsChanges,
): number => {
  const { baselineForecast, enabledSalesMetrics, adjustments } = variant

  const appliedAdjustments = sumBy(
    Object.entries(adjustments).filter(([k]) => isMetricEffective(enabledSalesMetrics, k)),
    ([, v]) => v,
  )

  return appliedAdjustments + baselineForecast
}

const calculateAverageProductTrendDecimal = (
  variant: IVariantSummary &
    MapValue<NonNullable<ReturnType<typeof useGetDemandVariantMetrics>["data"]>> & {
      enabledSalesMetrics: string[]
    },
): number => {
  const { trends } = variant
  const enabledTrends = Object.entries(trends)
    .filter(([k, v]) => variant.enabledSalesMetrics.includes(k) && v != null)
    .map(([, v]) => v)
  if (enabledTrends.length === 0) return 0
  return enabledTrends.reduce((acc, trend) => acc + trend, 0) / enabledTrends.length
}

const calculateVariantTotal = (
  variant: IVariantSummary &
    DemandMetrics &
    TrimMetrics &
    BaselineMetrics & {
      enabledSalesMetrics: string[]
      adjustedBaseline: number
      totalTrendAdjustment: number
    },
): number => {
  const { enabledSalesMetrics, adjustedBaseline, totalTrendAdjustment } = variant
  if (enabledSalesMetrics.includes("aiForecast")) {
    return (
      variant.aiSalesForecast +
      variant.preOrderQuantitySource +
      variant.expectedPreOrders -
      variant.inboundPreorderQuantitySource
    )
  } else {
    return adjustedBaseline + totalTrendAdjustment
  }
}

export const inferBaselineMetrics = (
  variantDemandMetrics: MapValue<
    NonNullable<ReturnType<typeof useGetDemandVariantMetrics>["data"]>
  >,
): BaselineMetrics => {
  for (const strategyKey of rankedBaselineStrategies) {
    const baselineStrategyMetrics = (variantDemandMetrics as Record<string, unknown>)[
      strategyKey
    ] as
      | undefined
      | Omit<
          VariantBaselineMetrics & { avgSimilarity?: number; nrMatches?: number },
          "forecastLevel" | "variantId"
        >
    if (baselineStrategyMetrics != undefined) {
      const {
        quantity: baselineForecast,
        preOrderQuantity: preOrderQuantityBaseline,
        expectedPreOrders,
        addedQuantityDiscount,
        addedQuantityMissingCollection,
        addedQuantityPreOrder,
        addedQuantityStockOut,
      } = baselineStrategyMetrics
      return {
        baselineForecast,
        preOrderQuantityBaseline,
        expectedPreOrders: expectedPreOrders ?? 0,
        type:
          strategyKey === BaselineMethodKey.CarryOver ||
          strategyKey === BaselineMethodKey.CarryOverFromOtherCollections
            ? "Carry-over"
            : "New",
        baselineMethod: strategyKey,
        avgSimilarity: baselineStrategyMetrics.avgSimilarity ?? 1,
        baselineMatchCount: baselineStrategyMetrics.nrMatches,
        adjustments: {
          addedQuantityDiscount,
          addedQuantityMissingCollection,
          addedQuantityPreOrder,
          addedQuantityStockOut,
        },
      }
    }
  }
  return {
    baselineForecast: 0,
    preOrderQuantityBaseline: 0,
    expectedPreOrders: 0,
    adjustments: {},
    avgSimilarity: 0,
    type: "New",
    baselineMethod: "none",
  }
}
